import { useEffect, useMemo, useState } from 'react';
import { CatalogApp, listAppCatalog, extractApiError } from '../services/api';

/**
 * App-Marketplace Katalog-Ansicht (Operator).
 *
 * Echte, durchsuchbare Katalog-Sicht der verfuegbaren Marketplace-Apps —
 * distinkt von der read-only Fleet-Coverage-Matrix (/marketplace-fleet) und
 * vom Service-Paket-Panel (nur Core/Module/Bundles). Adressiert "im Platform
 * Admin werden nur die Bundles angezeigt".
 *
 * Read-only: der Deploy einer einzelnen App in einen Tenant laeuft ueber die
 * App-Anfrage-Queue (Self-Service-Anfrage → Operator-Freigabe), nicht von
 * hier aus.
 *
 * Backend: GET /api/catalog (read-through-Proxy zum app-marketplace).
 */

const CATEGORY_LABELS: Record<string, string> = {
  erp: 'ERP',
  comm: 'Kommunikation',
  security: 'Security',
  productivity: 'Produktivität',
  observability: 'Observability',
  infra: 'Infrastruktur',
};

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat;
}

export function AppMarketplacePage() {
  const [apps, setApps] = useState<CatalogApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');

  useEffect(() => {
    listAppCatalog()
      .then((a) => { setApps(a); setError(null); })
      .catch((err) => setError(extractApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(apps.map((a) => a.category))).sort(),
    [apps],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps.filter((a) => {
      if (category !== 'all' && a.category !== category) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.slug.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q)
      );
    });
  }, [apps, search, category]);

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogApp[]>();
    for (const a of filtered) {
      const list = map.get(a.category) ?? [];
      list.push(a);
      map.set(a.category, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">App Marketplace (Katalog)</h2>
          <p className="text-sm text-slate-500">
            Verfügbare Marketplace-Apps. Read-only — der Deploy in einen Tenant läuft über die{' '}
            <span className="font-medium">App-Anfragen</span>-Queue (Self-Service-Anfrage → Freigabe).
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche…"
            aria-label="App-Suche"
            className="px-2 py-1 rounded border text-sm"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-2 py-1 rounded border text-sm"
            aria-label="Kategorie-Filter"
          >
            <option value="all">Alle Kategorien</option>
            {categories.map((c) => (
              <option key={c} value={c}>{categoryLabel(c)}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500">Lade Katalog…</p>
      ) : grouped.length === 0 ? (
        <p className="text-slate-500">Keine Apps für diesen Filter.</p>
      ) : (
        grouped.map(([cat, list]) => (
          <section key={cat} className="mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">
              {categoryLabel(cat)} <span className="text-slate-400">({list.length})</span>
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((a) => (
                <div key={a.slug} className="bg-white shadow rounded p-4 flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{a.name}</span>
                    {a.isBase && <span className="badge bg-slate-200 text-xs">Base</span>}
                  </div>
                  <span className="text-xs font-mono text-slate-400">{a.slug}</span>
                  <p className="text-sm text-slate-600 mt-2 flex-1">{a.description}</p>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {a.deploymentTargets.map((t) => (
                      <span key={t} className="badge bg-sky-100 text-sky-800 text-xs">{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
      <p className="text-xs text-slate-400 mt-2">{filtered.length} von {apps.length} App(s).</p>
    </div>
  );
}
